/**
 * ToolEditorPage — /agents/tools/:toolName
 * Tool detail on the DetailPage template. Demo page: /profile (title, tabs, side rail). The tab is kept in the URL;
 * the configuration tab is the edit form (entity-management §3).
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getToolOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Card, QueryBoundary } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { EntityVersionHistory } from '../../components/entities/EntityVersionHistory.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../help/index.js';
import { ToolConfigurationTab } from './tool-detail/ToolConfigurationTab.js';
import { ToolRail } from './tool-detail/ToolRail.js';

const TABS = [
  { id: 'configuration', label: 'Configuration' },
  { id: 'versions', label: 'Versions' },
] as const;

export const ToolEditorPage: React.FC = () => {
  const { toolName = '' } = useParams<{ toolName: string }>();
  const query = useQuery({ ...getToolOptions({ path: { name: toolName } }), enabled: toolName !== '' });
  const [tab, setTab] = useTabParam(['configuration', 'versions'] as const, 'configuration');

  return (
    <QueryBoundary query={query} label="the tool">
      {({ tool }) => (
        <DetailPage
          title={tool.name}
          subtitle={tool.description ?? undefined}
          breadcrumbs={[
            { label: 'Libraries', to: '/agents/libraries' },
            { label: 'Tools', to: '/agents/tools' },
            { label: tool.name },
          ]}
          helpContent={PAGE_HELP['tools']}
          badges={
            <>
              <Badge variant={tool.deprecated ? 'warning' : 'success'} dot>
                {tool.deprecated ? 'Deprecated' : 'Active'}
              </Badge>
              {tool.version && <Badge variant="info">Version {tool.version}</Badge>}
            </>
          }
          tabs={TABS}
          activeTab={tab}
          onTabChange={setTab}
          rail={<ToolRail tool={tool} />}
        >
          {tab === 'configuration' && <ToolConfigurationTab tool={tool} />}
          {tab === 'versions' && (
            <Card title="Version history">
              <EntityVersionHistory entityId={tool.entityId} />
            </Card>
          )}
        </DetailPage>
      )}
    </QueryBoundary>
  );
};
